// Explore-stats page: Bills on the floor (#215).
// Ranked table of bills with the most floor time (Q-resolved via the
// bills index for title + portfolio + originating chamber). Each row
// links to /bills/<bill_id> (the get_bill endpoint now falls back to
// a `bill_id` keyword query when the route param isn't a direct ES _id,
// so the existing md5-`_id` path keeps working too).
//
// Source: live_minutes.items[].is_bill==true, grouped by items.bill_id.
// Status field run through the #198 _normalise_status workaround on the
// backend.

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent, Stack, Link, Alert,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, PALETTE } from './charts';

function StatCard({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function BillsOnTheFloor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('bills-on-the-floor');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const top = c.top_bills || [];
  const amend = c.amend_split || {};
  const cov = c.coverage || {};

  return (
    <ExploreStatsPage
      title="Bills on the floor"
      description="Bills ranked by floor time — minutes the chamber spent on each bill in the captured `live_minutes` window. Bill titles/portfolios resolved live from the `bills` index. Status keywords pass through a normaliser that de-duplicates the known trailing-space + typo variants (e.g. 'Bill passed without amendents' → 'Bill passed without amendments') — workaround for #198."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="bills-on-the-floor"
    >
      {data && (
        <Box>
          {cov.bills > 0 && cov.with_titles < cov.bills && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {cov.with_titles} of {cov.bills} bill IDs resolved to a title
              in the bills index. Missing rows render the bare bill_id and
              still link through (the bill detail page can resolve either
              an ES _id or a bill_id keyword).
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCard label="Bills on the floor"
                        value={cov.bills}
                        sub={`${cov.appearances || 0} appearances`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Passed without amendments"
                        value={amend.without_amendments} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Passed with amendments"
                        value={amend.with_amendments} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Other / in progress"
                        value={amend.other} />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Top bills by floor time (minutes)
                </Typography>
                <StatBar data={top.slice(0, 12).map((b) => ({
                  bill: b.title || b.bill_id,
                  mins: b.mins,
                }))} xKey="bill" yKey="mins" layout="vertical" height={420} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Floor time by portfolio (minutes)
                </Typography>
                <StatBar data={(c.by_portfolio || []).slice(0, 12)
                  .map((r) => ({ portfolio: r.portfolio, mins: r.mins }))}
                         xKey="portfolio" yKey="mins"
                         layout="vertical" height={400} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Status mix (count of bill appearances)
                </Typography>
                <StatBar data={(c.status_mix || []).slice(0, 12)
                  .map((r) => ({ status: r.status, count: r.count }))}
                         xKey="status" yKey="count"
                         layout="vertical" height={400} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Time spent per status (proxy for stage time)
                </Typography>
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mb: 1 }}>
                  Minutes the chamber spent on bill items resolving to each
                  status. Many `live_minutes` items carry status as the
                  outcome captured for a single appearance, not the
                  conventional 1R/2R/Committee/3R stage label — this is the
                  best stage proxy the source data supports today.
                </Typography>
                <StatBar data={(c.stage_split || []).slice(0, 12)
                  .map((r) => ({ status: r.status, mins: r.mins }))}
                         xKey="status" yKey="mins"
                         layout="vertical" height={360} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Top bills — quick view
                </Typography>
                <Stack spacing={1}>
                  {top.slice(0, 10).map((b) => (
                    <Box key={b.bill_id} sx={{ display: 'flex', gap: 2,
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                pb: 1, alignItems: 'baseline' }}>
                      <Box sx={{ minWidth: 100, fontVariantNumeric: 'tabular-nums',
                                  fontSize: 12 }}>
                        <Link component={RouterLink}
                              to={`/bills/${b.bill_id}`}
                              underline="hover">{b.bill_id}</Link>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                          {b.title || '(title not in bills index)'}
                        </Typography>
                        {b.portfolio && (
                          <Typography variant="caption" color="text.secondary">
                            {b.portfolio} · {b.originating_chamber}
                            {b.statuses && b.statuses.length
                              ? ' · ' + b.statuses.join(', ')
                              : ''}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ minWidth: 90, textAlign: 'right' }}>
                        {b.mins} min
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
